
<template>
  <section id="location" class="py-5 bg-soft">
    <div class="container">
      <h2 class="text-center textprimary fw-bold mb-3">
        Our Locations
      </h2>

      <p class="text-center text-muted mb-5">
        Find us at any of our locations.
      </p>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border textprimary" role="status"></div>
        <p class="text-muted mt-3">Loading locations...</p>
      </div>

      <div v-else-if="error" class="alert alert-warning text-center">
        {{ error }}
      </div>

      <div v-else-if="locations.length === 0" class="text-center py-4">
        <p class="text-muted">No locations available at the moment.</p>
      </div>

      <div
        v-else
        class="locations-carousel"
        @mouseenter="stopAutoSlide"
        @mouseleave="startAutoSlide"
      >
        <div class="row g-4 align-items-center">
          <!-- LOCATION DETAILS -->
          <div class="col-lg-5">
            <div :key="currentLocation.id || currentIndex" class="location-card">
              <div class="location-icon">
                <i class="bi bi-geo-alt-fill"></i>
              </div>

              <h3 class="fw-bold textprimary mb-3">
                {{ currentLocation.name }}
              </h3>

              <p class="text-muted mb-4">
                {{
                  currentLocation.description ||
                  'Visit us at this location. We are ready to serve you.'
                }}
              </p>

              <div class="location-detail">
                <i class="bi bi-pin-map-fill"></i>
                <div>
                  <strong>Address</strong>
                  <span>
                    {{ currentLocation.address || 'Address not provided' }}
                  </span>
                </div>
              </div>

              <div
                v-if="hasCoordinates(currentLocation)"
                class="location-detail"
              >
                <i class="bi bi-geo-fill"></i>
                <div>
                  <strong>Coordinates</strong>
                  <span>
                    {{ currentLocation.latitude }},
                    {{ currentLocation.longitude }}
                  </span>
                </div>
              </div>

              <a
                :href="getMapUrl(currentLocation)"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btnprimary rounded-pill px-4 mt-3"
              >
                <i class="bi bi-map me-2"></i>
                Get Directions
              </a>
            </div>
          </div>

          <!-- MAP -->
          <div class="col-lg-7">
            <div :key="currentLocation.id || currentIndex" class="map-card">
              <iframe
                :src="getEmbedUrl(currentLocation)"
                width="100%"
                height="400"
                style="border: 0"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
                :title="'Map of ' + currentLocation.name"
              ></iframe>
            </div>
          </div>
        </div>

        <!-- CAROUSEL CONTROLS -->
        <div v-if="locations.length > 1" class="location-controls">
          <button
            type="button"
            class="location-arrow"
            aria-label="Previous location"
            @click="previousLocation"
          >
            <i class="bi bi-chevron-left"></i>
          </button>

          <div class="location-indicators">
            <button
              v-for="(location, index) in locations"
              :key="location.id || index"
              type="button"
              :class="{ active: index === currentIndex }"
              :aria-label="'Show ' + location.name"
              :aria-current="index === currentIndex ? 'true' : null"
              @click="goToLocation(index)"
            ></button>
          </div>

          <button
            type="button"
            class="location-arrow"
            aria-label="Next location"
            @click="nextLocation"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>

        <p v-if="locations.length > 1" class="location-count">
          {{ currentIndex + 1 }} of {{ locations.length }} locations
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import Config from '../components/Helpers/Config';

export default {
  data() {
    return {
      locations: [],
      loading: false,
      error: '',
      currentIndex: 0,
      slideTimer: null
    };
  },

  computed: {
    currentLocation() {
      return this.locations[this.currentIndex] || {
        id: null,
        name: 'Our Location',
        description: '',
        address: '',
        latitude: '',
        longitude: ''
      };
    }
  },

  mounted() {
    this.fetchLocations();
  },

  beforeUnmount() {
    this.stopAutoSlide();
  },

  methods: {
    async fetchLocations() {
      this.loading = true;
      this.error = '';

      try {
        const response = await fetch(
          `${Config.API_BASE_URL}/market_locations`
        );

        if (!response.ok) {
          throw new Error('Failed to load locations.');
        }

        const result = await response.json();

        const items = Array.isArray(result)
          ? result
          : Array.isArray(result.data)
            ? result.data
            : Array.isArray(result.locations)
              ? result.locations
              : [];

        this.locations = items.map((item) => ({
          id: item.id,
          name: item.name ?? item.location_name ?? 'Our Location',
          description: item.description ?? '',
          address: item.address ?? '',
          latitude: item.latitude ?? '',
          longitude: item.longitude ?? ''
        }));

        this.currentIndex = 0;
        this.startAutoSlide();
      } catch (error) {
        console.error('Error fetching locations:', error);
        this.error = 'Unable to load locations. Please try again later.';
      } finally {
        this.loading = false;
      }
    },

    startAutoSlide() {
      this.stopAutoSlide();

      if (this.locations.length > 1) {
        this.slideTimer = setInterval(() => {
          this.currentIndex =
            (this.currentIndex + 1) % this.locations.length;
        }, 5000);
      }
    },

    stopAutoSlide() {
      if (this.slideTimer !== null) {
        clearInterval(this.slideTimer);
        this.slideTimer = null;
      }
    },

    previousLocation() {
      if (this.locations.length <= 1) return;

      this.currentIndex =
        (this.currentIndex - 1 + this.locations.length) %
        this.locations.length;

      this.startAutoSlide();
    },

    nextLocation() {
      if (this.locations.length <= 1) return;

      this.currentIndex =
        (this.currentIndex + 1) % this.locations.length;

      this.startAutoSlide();
    },

    goToLocation(index) {
      if (index < 0 || index >= this.locations.length) return;

      this.currentIndex = index;
      this.startAutoSlide();
    },

    hasCoordinates(location) {
      return (
        location.latitude !== '' &&
        location.longitude !== '' &&
        location.latitude != null &&
        location.longitude != null
      );
    },

    getEmbedUrl(location) {
      const query = this.hasCoordinates(location)
        ? `${location.latitude},${location.longitude}`
        : location.address || location.name;

      return `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;
    },

    getMapUrl(location) {
      const query = this.hasCoordinates(location)
        ? `${location.latitude},${location.longitude}`
        : location.address || location.name;

      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    }
  }
};
</script>

<style scoped>
.bg-soft {
  background: #f6fbf8;
}

/* CAROUSEL */
.locations-carousel {
  position: relative;
}

/* LOCATION CARD */
.location-card {
  background: #ffffff;
  padding: 40px 35px;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(25, 135, 84, 0.08);
  height: 100%;
  animation: locationFade 0.35s ease;
}

.location-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary-color);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-bottom: 20px;
}

.location-detail {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 18px;
  overflow-wrap: anywhere;
}

.location-detail > i {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 50%;
  background: #e9f7ef;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
}

.location-detail div {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.location-detail strong {
  font-size: 14px;
  color: #333;
}

.location-detail span {
  font-size: 13px;
  color: #777;
}

/* MAP */
.map-card {
  overflow: hidden;
  border-radius: 24px;
  background: #fff;
  padding: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  animation: locationFade 0.35s ease;
}

.map-card iframe {
  display: block;
  width: 100%;
  height: 400px;
  border: 0;
  border-radius: 18px;
}

/* CONTROLS */
.location-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 22px;
  margin-top: 30px;
}

.location-arrow {
  width: 40px;
  height: 40px;
  border: 1px solid var(--primary-color);
  border-radius: 50%;
  background: #fff;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.location-arrow:hover {
  background: var(--primary-color);
  color: #fff;
}

.location-indicators {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 9px;
}

.location-indicators button {
  width: 9px;
  height: 9px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #c8d8ce;
  cursor: pointer;
  transition: width 0.2s ease, background 0.2s ease;
}

.location-indicators button.active {
  width: 25px;
  border-radius: 10px;
  background: var(--primary-color);
}

.location-count {
  text-align: center;
  color: #777;
  font-size: 13px;
  margin: 12px 0 0;
}

@keyframes locationFade {
  from {
    opacity: 0.5;
    transform: translateY(5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* TABLET */
@media (max-width: 991px) {
  .location-card {
    padding: 30px 25px;
  }

  .map-card iframe {
    height: 350px;
  }
}

/* MOBILE */
@media (max-width: 576px) {
  .location-card {
    padding: 25px 20px;
    border-radius: 20px;
  }

  .map-card {
    border-radius: 20px;
  }

  .map-card iframe {
    height: 300px;
    border-radius: 14px;
  }

  .location-controls {
    gap: 15px;
  }
}
</style>
